import { createContext, ReactNode, useCallback, use, useMemo } from "react";

interface ExcludedPeriodsContextValue {
  excludedPeriodIds: Set<number>;
  togglePeriods: (periodIds: number[]) => void;
  isExcluded: (periodId: number) => boolean;
  areAllExcluded: (periodIds: number[]) => boolean;
  // Whether this surface's exclusion controls are unavailable, covering the material and
  // timing controls as well as visibility, since all three re-merge the same surface. Set
  // once the surface has unsaved edits a re-merge would discard, and by the bring-in modal
  // for carried ancestor edits as well, which are matched onto the merge by content-derived id.
  exclusionsLocked: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const NOOP_VALUE: ExcludedPeriodsContextValue = {
  excludedPeriodIds: new Set(),
  togglePeriods: noop,
  isExcluded: () => false,
  areAllExcluded: () => false,
  exclusionsLocked: false,
};

const ExcludedPeriodsContext =
  createContext<ExcludedPeriodsContextValue | null>(null);

export const useExcludedPeriods = (): ExcludedPeriodsContextValue => {
  const context = use(ExcludedPeriodsContext);
  return context ?? NOOP_VALUE;
};

export const ExcludedPeriodsProvider = ({
  excludedPeriodIds,
  setExcludedPeriodIds,
  exclusionsLocked = false,
  children,
}: {
  excludedPeriodIds: Set<number>;
  setExcludedPeriodIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  exclusionsLocked?: boolean;
  children: ReactNode;
}) => {
  const togglePeriods = useCallback(
    (periodIds: number[]) => {
      if (exclusionsLocked) return;
      setExcludedPeriodIds((prev) => {
        const next = new Set(prev);
        const allExcluded = periodIds.every((id) => next.has(id));
        if (allExcluded) {
          periodIds.forEach((id) => next.delete(id));
        } else {
          periodIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [setExcludedPeriodIds, exclusionsLocked]
  );

  const isExcluded = useCallback(
    (periodId: number) => excludedPeriodIds.has(periodId),
    [excludedPeriodIds]
  );

  const areAllExcluded = useCallback(
    (periodIds: number[]) =>
      periodIds.length > 0 &&
      periodIds.every((id) => excludedPeriodIds.has(id)),
    [excludedPeriodIds]
  );

  const value = useMemo(
    () => ({
      excludedPeriodIds,
      togglePeriods,
      isExcluded,
      areAllExcluded,
      exclusionsLocked,
    }),
    [
      excludedPeriodIds,
      togglePeriods,
      isExcluded,
      areAllExcluded,
      exclusionsLocked,
    ]
  );

  return (
    <ExcludedPeriodsContext value={value}>{children}</ExcludedPeriodsContext>
  );
};
